import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider } from './contexts/AuthContext';
import { NotificationProvider } from './contexts/NotificationContext';
import { RealtimeProvider } from './contexts/RealtimeContext';
import { SidebarProvider } from './contexts/SidebarContext';
import Layout from './components/Layout';
import { ErrorBoundary } from './components/ErrorBoundary';
import ProtectedRoute from './components/ProtectedRoute';
import Login from './pages/Login';
import NotFound from './pages/NotFound';
// Lazy load pages for better performance and faster initial load
const Dashboard = lazy(() => import('./pages/Dashboard'));
const Users = lazy(() => import('./pages/Users'));
const UsersMap = lazy(() => import('./pages/UsersMap'));
const UserDetails = lazy(() => import('./pages/UserDetails'));
const Services = lazy(() => import('./pages/Services'));
const Bookings = lazy(() => import('./pages/Bookings'));
const Professionals = lazy(() => import('./pages/Professionals'));
const ProfessionalDetails = lazy(() => import('./pages/ProfessionalDetails'));
const EditProfessional = lazy(() => import('./pages/EditProfessional'));
const AddWorker = lazy(() => import('./pages/AddWorker'));
const Hubs = lazy(() => import('./pages/Hubs'));
const BlogPosts = lazy(() => import('./pages/BlogPosts'));
const BlogPostDetails = lazy(() => import('./pages/BlogPostDetails'));
const Notifications = lazy(() => import('./pages/Notifications'));
const Analytics = lazy(() => import('./pages/Analytics'));
const Payments = lazy(() => import('./pages/Payments'));
const ReferralAnalytics = lazy(() => import('./pages/ReferralAnalytics'));
const ServiceCategories = lazy(() => import('./pages/ServiceCategories'));
const ServiceOrdering = lazy(() => import('./pages/ServiceOrdering'));
const Content = lazy(() => import('./pages/Content'));
const AuditLogs = lazy(() => import('./pages/AuditLogs'));
const Slideshow = lazy(() => import('./pages/Slideshow'));
const BlogCategories = lazy(() => import('./pages/BlogCategories'));
const Coupons = lazy(() => import('./pages/Coupons'));
const CustomerExperience = lazy(() => import('./pages/CustomerExperience'));
const RatingsReviews = lazy(() => import('./pages/RatingsReviews'));
const AdminAccess = lazy(() => import('./pages/AdminAccess'));
const ManualLeads = lazy(() => import('./pages/ManualLeads'));
const WorkerRegistrationDetails = lazy(() => import('./pages/WorkerRegistrationDetails'));
const PaymentGateway = lazy(() => import('./pages/PaymentGateway'));
const WorkerApprovals = lazy(() => import('./pages/WorkerApprovals'));

// Loading component for lazy loaded routes
const LoadingFallback = () => (
  <div className="flex items-center justify-center h-64">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white"></div>
  </div>
);

// Create a client with optimized settings for performance
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
      staleTime: 5 * 60 * 1000, // 5 minutes
      gcTime: 10 * 60 * 1000, // 10 minutes (formerly cacheTime)
      refetchOnMount: false, // Don't refetch on mount if data is fresh
      refetchOnReconnect: true, // Refetch on reconnect
    },
  },
});

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <NotificationProvider>
          <RealtimeProvider>
            <SidebarProvider>
              <ErrorBoundary>
                <Router>
                  <div className="min-h-screen bg-background text-text">
                    <Routes>
                      <Route path="/login" element={<Login />} />
                      <Route path="/*" element={
                        <Layout>
                          <Suspense fallback={LoadingFallback()}>
                            <Routes>
                              <Route path="/" element={<ProtectedRoute requirePermissions={['dashboard.read']}><Dashboard /></ProtectedRoute>} />
                              <Route path="/users" element={<ProtectedRoute requirePermissions={['users.read']}><Users /></ProtectedRoute>} />
                              <Route path="/users-map" element={<ProtectedRoute requirePermissions={['users.read']}><UsersMap /></ProtectedRoute>} />
                              <Route path="/users/:id" element={<ProtectedRoute requirePermissions={['users.read']}><UserDetails /></ProtectedRoute>} />
                              <Route path="/services/*" element={<ProtectedRoute requirePermissions={['services.read']}><Services /></ProtectedRoute>} />
                              <Route path="/service-categories" element={<ProtectedRoute requirePermissions={['serviceCategories.read']}><ServiceCategories /></ProtectedRoute>} />
                              <Route path="/ordering" element={<ProtectedRoute requirePermissions={['services.write']}><ServiceOrdering /></ProtectedRoute>} />
                              <Route path="/bookings/*" element={<ProtectedRoute requirePermissions={['bookings.read']}><Bookings /></ProtectedRoute>} />
                              <Route path="/professionals" element={<ProtectedRoute requirePermissions={['professionals.read']}><Professionals /></ProtectedRoute>} />
                              <Route path="/professionals/add" element={<ProtectedRoute requirePermissions={['professionals.write']}><AddWorker /></ProtectedRoute>} />
                              <Route path="/professionals/:id" element={<ProtectedRoute requirePermissions={['professionals.read']}><ProfessionalDetails /></ProtectedRoute>} />
                              <Route path="/professionals/:id/edit" element={<ProtectedRoute requirePermissions={['professionals.write']}><EditProfessional /></ProtectedRoute>} />
                              <Route path="/worker-approvals" element={<ProtectedRoute requirePermissions={['workers.approve']}><WorkerApprovals /></ProtectedRoute>} />
                              <Route path="/worker-registration/:id" element={<ProtectedRoute requirePermissions={['workers.approve']}><WorkerRegistrationDetails /></ProtectedRoute>} />
                              <Route path="/hubs" element={<ProtectedRoute requirePermissions={['professionals.write']}><Hubs /></ProtectedRoute>} />
                              <Route path="/manual-leads" element={<ProtectedRoute requirePermissions={['bookings.write']}><ManualLeads /></ProtectedRoute>} />
                              <Route path="/blog/*" element={<ProtectedRoute requirePermissions={['blog.read']}><BlogPosts /></ProtectedRoute>} />
                              <Route path="/blog/categories" element={<ProtectedRoute requirePermissions={['blog.categories.read']}><BlogCategories /></ProtectedRoute>} />
                              <Route path="/slideshow/*" element={<ProtectedRoute requirePermissions={['slideshow.read']}><Slideshow /></ProtectedRoute>} />
                              <Route path="/coupons" element={<ProtectedRoute requirePermissions={['coupons.read']}><Coupons /></ProtectedRoute>} />
                              <Route path="/cx" element={<ProtectedRoute requirePermissions={['support.write']}><CustomerExperience /></ProtectedRoute>} />
                              <Route path="/reviews" element={<ProtectedRoute requirePermissions={['reviews.read']}><RatingsReviews /></ProtectedRoute>} />
                              <Route path="/notifications/*" element={<ProtectedRoute requirePermissions={['notifications.write']}><Notifications /></ProtectedRoute>} />
                              <Route path="/payment-gateway" element={<ProtectedRoute requirePermissions={['settings.write']}><PaymentGateway /></ProtectedRoute>} />
                              <Route path="/admin-access" element={<ProtectedRoute allowRoles={['super-admin']}><AdminAccess /></ProtectedRoute>} />
                              <Route path="/analytics" element={<ProtectedRoute requirePermissions={['analytics.read']}><Analytics /></ProtectedRoute>} />
                              <Route path="/payments" element={<ProtectedRoute requirePermissions={['payments.read']}><Payments /></ProtectedRoute>} />
                              <Route path="/referrals" element={<ProtectedRoute requirePermissions={['referrals.read']}><ReferralAnalytics /></ProtectedRoute>} />
                              <Route path="/content" element={<ProtectedRoute requirePermissions={['blog.read']}><Content /></ProtectedRoute>} />
                              <Route path="/audit" element={<ProtectedRoute allowRoles={['super-admin']}><AuditLogs /></ProtectedRoute>} />
                              <Route path="*" element={<NotFound />} />
                            </Routes>
                          </Suspense>
                        </Layout>
                      } />
                    </Routes>
                  </div>
                </Router>
              </ErrorBoundary>
            </SidebarProvider>
          </RealtimeProvider>
        </NotificationProvider>
      </AuthProvider>
    </QueryClientProvider>
  );
}

export default App;
